import type { SocialButtonProps } from 'app/components/authentication/LoginButtonState';
import appleIcon from 'app/design/images/figma-login/apple.svg';
import facebookIcon from 'app/design/images/figma-login/facebook.svg';
import googleIcon from 'app/design/images/figma-login/google-composite.svg';
import { Row } from 'app/design/primitives/box';
import { Button, PressableType } from 'app/design/primitives/button';
import { PCImage } from 'app/design/primitives/image';
import { Text } from 'app/design/primitives/text';
import { useTheme } from 'app/shopify';
import { useTranslation } from 'react-i18next';
import { ActivityIndicator } from 'react-native';

type Provider = 'Apple' | 'Facebook' | 'Google';

const icons = { Apple: appleIcon, Facebook: facebookIcon, Google: googleIcon };
const labels = {
  Apple: 'continueWithApple',
  Facebook: 'continueWithFacebook',
  Google: 'continueWithGoogle',
} as const;

export function SocialAuthButton({
  provider,
  iconOnly = false,
  loginButtonsState,
  onPress,
}: Pick<SocialButtonProps, 'iconOnly' | 'loginButtonsState'> & { provider: Provider; onPress: () => void }) {
  const { t } = useTranslation('signup');
  const theme = useTheme();
  const { loginButtons } = loginButtonsState;
  const loading = loginButtons[provider];

  return (
    <Button
      accessibilityLabel={t(labels[provider])}
      accessibilityRole="button"
      disabled={Object.values(loginButtons).includes(true)}
      onPress={onPress}
      pressableType={PressableType.GestureHandler}
      style={{
        backgroundColor: theme.colors['surface.default'],
        borderColor: theme.colors['border.secondary'],
        borderWidth: theme.borderWidths['border-width.subtle'],
        borderRadius: theme.borderRadii['radius.full'],
        height: 60,
        width: '100%',
      }}
      variant="plain"
    >
      {loading ? (
        <ActivityIndicator color={theme.colors['text.primary']} />
      ) : (
        <Row alignItems="center" gap="spacing.general.x3" justifyContent="center">
          <PCImage
            contentFit="contain"
            width={theme.spacing['spacing.general.x7']}
            height={theme.spacing['spacing.general.x7']}
            source={icons[provider]}
          />
          {!iconOnly ? (
            <Text color="text.primary" fontSize={14} fontWeight="700">
              {t(labels[provider])}
            </Text>
          ) : null}
        </Row>
      )}
    </Button>
  );
}
